<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/order/PosOrderItemsGrid.vue.
  import { IItem } from '@/interfaces/IItem';
  import { usePosMoney } from '@/composition/use-pos-money';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import PosV2ItemImage from './PosV2ItemImage.vue';

  withDefaults(
    defineProps<{
      items: IItem[];
      loading?: boolean;
      emptyText?: string;
      emptyIcon?: string;
      /** Items rendered greyed with a "Deshabilitado" badge (still clickable; guard lives in the workspace). */
      disabledItemIds?: Set<number>;
    }>(),
    {
      disabledItemIds: () => new Set<number>(),
    }
  );

  const emit = defineEmits<{
    (e: 'item-click', item: IItem): void;
  }>();

  const money = usePosMoney();
</script>

<template>
  <PosUiScrollable
    class="pos-v2-items-grid-container"
    orientation="vertical"
    variant="overlay"
    theme="default"
    size="md"
    hide-scrollbar="touch"
  >
    <div class="items-grid">
      <!-- Loading -->
      <template v-if="loading">
        <div v-for="i in 12" :key="i" class="item-card item-card-skeleton">
          <div class="skeleton-image" />
          <div class="skeleton-content">
            <div class="skeleton-line" />
            <div class="skeleton-line short" />
            <div class="skeleton-line shorter" />
          </div>
        </div>
      </template>

      <!-- Empty -->
      <div v-else-if="items.length === 0" class="empty-state">
        <PosUiIcon :name="emptyIcon ?? 'package-variant'" class="empty-icon" />
        <p class="empty-text">
          {{ emptyText ?? $t('posV2.emptyCategory') }}
        </p>
      </div>

      <!-- Items -->
      <template v-else>
        <button
          v-for="item in items"
          :key="item.id"
          class="item-card"
          :class="{ 'item-card--disabled': disabledItemIds.has(item.id) }"
          type="button"
          @click="emit('item-click', item)"
        >
          <div class="item-image">
            <PosV2ItemImage :item="item" class="item-image-img" />
          </div>

          <div class="item-content">
            <h3 class="item-name">{{ item.name }}</h3>
            <p v-if="item.description" class="item-description">
              {{ item.description }}
            </p>

            <div class="item-footer">
              <div v-if="item.price > 0" class="item-price">
                <span v-if="item.discountedPrice" class="price-original">
                  {{ money.formatCatalogMoney(item.price) }}
                </span>
                <span class="price-current">
                  {{
                    money.formatCatalogMoney(item.discountedPrice || item.price)
                  }}
                </span>
              </div>
              <PosUiBadge
                v-if="disabledItemIds.has(item.id)"
                :label="$t('posV2.disabledBadge')"
                color="neutral"
                variant="soft"
                size="xs"
                class="disabled-badge"
              />
              <PosUiBadge
                v-if="item.hasOptions"
                :label="$t('posV2.optionsBadge')"
                color="info"
                variant="soft"
                size="xs"
                icon="tune"
                class="options-badge"
              />
            </div>
          </div>
        </button>
      </template>
    </div>
  </PosUiScrollable>
</template>

<style scoped lang="scss">
  .pos-v2-items-grid-container {
    @apply tw-relative tw-min-h-0 tw-flex-1;
  }

  .items-grid {
    @apply tw-grid tw-grid-cols-2 tw-gap-2 tw-p-2;

    // Container width drives the column count above mobile (the cart pane
    // eats viewport width, so viewport breakpoints overshoot). 280px leaves
    // ~180px of text beside the 80px photo: room for a name, a price and
    // the options badge on one line.
    @screen md {
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    }

    .item-card {
      @apply tw-flex tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-text-left tw-transition-all tw-duration-150 hover:tw-border-neutral-300 hover:tw-shadow-md active:tw-scale-[0.98] md:tw-h-[100px] md:tw-flex-row;

      .item-image {
        @apply tw-relative tw-aspect-square tw-flex-shrink-0 tw-overflow-hidden tw-bg-neutral-100 md:tw-aspect-auto md:tw-h-[100px] md:tw-w-[80px];
      }

      .item-image-img {
        @apply tw-h-full tw-w-full tw-object-cover tw-transition-transform tw-duration-150;
      }

      &:hover .item-image-img {
        @apply tw-scale-105;
      }

      // Name and description pinned to the top, footer to the bottom (its
      // mt-auto): names and prices line up across a row of cards.
      .item-content {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5 tw-overflow-hidden tw-p-2 md:tw-px-2.5;
      }

      .item-name {
        @apply tw-line-clamp-2 tw-flex-shrink-0 tw-text-sm tw-font-semibold tw-leading-tight tw-text-neutral-900 md:tw-line-clamp-1;
      }

      // mb-0: Vuetify's global `.v-application p` margin (16px) would take
      // the fixed-height card's room and squash this line to a sliver.
      .item-description {
        @apply tw-mb-0 tw-hidden tw-overflow-hidden tw-text-xs tw-leading-tight tw-text-neutral-500 md:tw-block md:tw-truncate;
      }

      // Wraps: badges that do not fit drop below the price, never clipped.
      .item-footer {
        @apply tw-mt-auto tw-flex tw-flex-shrink-0 tw-flex-wrap tw-items-center tw-justify-between tw-gap-1;
      }

      // nowrap: the currency symbol never splits from the amount.
      .item-price {
        @apply tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap;
      }

      .options-badge {
        @apply tw-ml-auto;
      }

      .price-original {
        @apply tw-text-xs tw-text-neutral-500 tw-line-through;
      }

      // Catalog prices read as data, not as CTAs: dark + tabular.
      .price-current {
        @apply tw-text-sm tw-font-semibold tw-tabular-nums tw-text-neutral-900 md:tw-text-base;
      }

      .disabled-badge {
        @apply tw-flex-shrink-0;
      }

      // Card stays clickable: the workspace guard toast explains the block.
      &.item-card--disabled {
        @apply tw-bg-neutral-100 hover:tw-border-neutral-200 hover:tw-shadow-none;

        .item-name,
        .item-description,
        .price-current,
        .price-original {
          @apply tw-text-neutral-400;
        }

        .item-image-img {
          @apply tw-opacity-50;
        }
      }
    }

    .item-card-skeleton {
      @apply tw-cursor-default hover:tw-border-neutral-200 hover:tw-shadow-none md:tw-h-[80px] md:tw-flex-row;

      .skeleton-image {
        @apply tw-aspect-square tw-animate-pulse tw-bg-neutral-100 md:tw-aspect-auto md:tw-h-[80px] md:tw-w-[80px] md:tw-flex-shrink-0;
      }

      .skeleton-content {
        @apply tw-flex tw-flex-col tw-gap-2 tw-p-2 md:tw-flex-1 md:tw-justify-center;
      }

      .skeleton-line {
        @apply tw-h-3 tw-animate-pulse tw-rounded tw-bg-neutral-100;

        &.short {
          @apply tw-w-3/4;
        }

        &.shorter {
          @apply tw-w-1/2;
        }
      }
    }

    .empty-state {
      @apply tw-col-span-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-py-12 tw-text-neutral-500;

      .empty-icon {
        @apply tw-text-5xl;
      }

      .empty-text {
        @apply tw-text-lg;
      }
    }
  }
</style>
